import { useMemo } from 'react';
import { useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import useQueryObject from '@hooks/useQueryObjectSql';
import { isNullOrUndefined } from '@utils/utils';
import { gql } from 'graphql-request';
import { GetRiskScoringConfigResponse } from './types';
import { getFindingTypeDisplayName } from './utils';

export const useGetRiskScoringConfig = ({ projectionName }: { projectionName: string }) =>
  useGqlQuery<GetRiskScoringConfigResponse>({
    queryKey: [QueryKey.GetRiskScoringConfig, projectionName],
    query: GET_RISK_SCORING_CONFIG,
    variables: { projectionName },
  });

export const useGetAllRiskScoringConfig = () =>
  useGqlQuery<GetRiskScoringConfigResponse>({
    queryKey: [QueryKey.GetAllRiskScoringConfig],
    query: GET_ALL_RISK_SCORING_CONFIG,
  });

export const useCreateOrUpdateRiskScoringConfig = () =>
  useGqlMutation({
    mutationKey: [QueryKey.CreateOrUpdateRiskScoringConfig],
    mutation: CREATE_OR_UPDATE_RISK_SCORE,
  });

const GET_ALL_RISK_SCORING_CONFIG = gql`
  query getAllRiskScoringConfig {
    getAllRiskScoringConfig {
      projectionToRiskScores {
        projectionName
        riskScore {
          id
          accountId
          projectionName
          updatedByUserId
          updatedAt
          config {
            baseScoreWeightContribution {
              projectionName
              weight
              aggType
            }
          }
        }
      }
      projectionToBaseScores {
        projectionName
        baseScore {
          id
          accountId
          projectionName
          config {
            configTypes {
              type
              isDefault
              factorGroups {
                name
                weight
                factors {
                  displayName
                  fieldName
                  fallback
                  scoringRules {
                    value
                    filter
                    adjustmentType
                  }
                }
              }
            }
          }
        }
      }
      riskScoreProjectionToBaseScoresMap {
        riskScoreProjectionName
        baseScores {
          projectionName
          allowedAggTypes
        }
      }
    }
  }
`;

const CREATE_OR_UPDATE_RISK_SCORE = gql`
  mutation createOrUpdateRiskScoringConfig($input: RiskScoringCreationRequestInput!) {
    createOrUpdateRiskScoringConfig(input: $input)
  }
`;

export const useFindingTypeOptions = (existingTypes: Set<string>) => {
  const { data: findingTypesData, isLoading: isLoadingFindingTypes } = useQueryObject({
    queryObject: {
      select: {
        dims: [
          {
            name: 'finding.type',
          },
        ],
        metrics: [],
      },
      distinct: true,
      projectionId: {
        name: 'uber_findings',
        builtIn: true,
      },
      sorting: [],
    },
  });

  const findingTypeOptions = useMemo(
    () =>
      (findingTypesData || [])
        .map(f => f['finding.type'])
        .filter(type => !isNullOrUndefined(type) && !existingTypes.has(type))
        .map(type => ({
          title: getFindingTypeDisplayName(type),
          value: type,
        })),
    [findingTypesData, existingTypes]
  );

  return { findingTypeOptions, isLoadingFindingTypes };
};

const GET_RISK_SCORING_CONFIG = gql`
  query getRiskScoringConfig($projectionName: String!) {
    getRiskScoringConfig(projectionName: $projectionName) {
      projectionToRiskScores {
        projectionName
        riskScore {
          id
          accountId
          projectionName
          updatedByUserId
          updatedAt
          config {
            baseScoreWeightContribution {
              projectionName
              weight
              aggType
            }
          }
        }
      }
      projectionToBaseScores {
        projectionName
        baseScore {
          id
          accountId
          projectionName
          config {
            configTypes {
              type
              factorGroups {
                name
                weight
                factors {
                  displayName
                  fieldName
                  fallback
                  scoringRules {
                    value
                    filter
                    adjustmentType
                  }
                }
              }
            }
          }
        }
      }
      riskScoreProjectionToBaseScoresMap {
        riskScoreProjectionName
        baseScores {
          projectionName
          allowedAggTypes
        }
      }
    }
  }
`;
